// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The rule expression evaluator (M2-08).
//
// A pure, side-effect-free tree over integers and booleans. No loops, no user functions, no
// recursion an author can write, no strings. Depth 16, 256 nodes per evaluation.
//
// **The bounds are the security model, not a performance tuning.** These expressions are
// authored by one player and evaluated on another player's device and on the server. An
// unbounded tree is a denial of service anybody could publish, so the limits are enforced
// twice: statically at load, where an over-budget expression is refused outright, and again
// here at runtime, where exceeding the budget yields `false` rather than continuing. The
// runtime check should be unreachable — it exists because "should be unreachable" is not a
// property a sandbox can rest on.
//
// Booleans are carried as 0 and 1 so that one evaluation function serves both, and so that
// `add` over comparison results — a genuinely useful way to count satisfied conditions —
// needs no special case.

import { EXPRESSION_OPERATORS } from '../machine/types'
import type { Expression } from '../machine/types'

export const MAX_DEPTH = 16
export const MAX_NODES = 256

/** Everything an expression may read. Nothing here can mutate anything. */
export interface EvalContext {
  /** Plain value of an int or bool, or the raw bitmask of a set. */
  varValue(id: string): number
  setCount(id: string): number
  setHas(id: string, member: string): boolean
  modeActive(id: string): boolean
  timerRunning(id: string): boolean
  ballsInPlay(): number
  playerBall(): number
  switchClosed(id: string): boolean
  /** 0 off, 1 on, 2+ for a named blink pattern. */
  lampState(id: string): number
}

class Budget {
  nodes = 0
  exceeded = false
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function operandList(arg: unknown): unknown[] {
  return Array.isArray(arg) ? arg : [arg]
}

/**
 * Evaluate an expression to a number. Booleans arrive and leave as 0 or 1.
 *
 * A malformed node yields 0 rather than throwing. Validation refuses malformed expressions
 * at load, so reaching that path means something already went wrong; throwing here would
 * turn a bad definition into a crashed game rather than a scoreless one.
 */
export function evaluate(node: Expression, ctx: EvalContext): number {
  const budget = new Budget()
  return evalNode(node, ctx, 1, budget)
}

/** Convenience for `when` clauses, which are conditions rather than values. */
export function evaluateCondition(node: Expression | undefined, ctx: EvalContext): boolean {
  if (node === undefined) return true
  return evaluate(node, ctx) !== 0
}

function evalNode(node: unknown, ctx: EvalContext, depth: number, budget: Budget): number {
  budget.nodes++
  if (budget.nodes > MAX_NODES || depth > MAX_DEPTH) {
    budget.exceeded = true
    return 0
  }
  if (typeof node === 'number') return node
  if (typeof node === 'boolean') return node ? 1 : 0
  if (!isObject(node)) return 0

  const keys = Object.keys(node)
  if (keys.length !== 1) return 0
  const op = keys[0]!
  if (!(EXPRESSION_OPERATORS as readonly string[]).includes(op)) return 0
  const arg = node[op]

  switch (op) {
    // --- leaves that name something ------------------------------------------
    case 'var':
      return typeof arg === 'string' ? ctx.varValue(arg) : 0
    case 'modeActive':
      return typeof arg === 'string' && ctx.modeActive(arg) ? 1 : 0
    case 'timerRunning':
      return typeof arg === 'string' && ctx.timerRunning(arg) ? 1 : 0
    case 'switchClosed':
      return typeof arg === 'string' && ctx.switchClosed(arg) ? 1 : 0
    case 'lampState':
      return typeof arg === 'string' ? ctx.lampState(arg) : 0
    case 'ballsInPlay':
      return ctx.ballsInPlay()
    case 'playerBall':
      return ctx.playerBall()

    // --- set queries -----------------------------------------------------------
    case 'count': {
      // `count` takes a set reference, not a value: the cardinality of a bitmask is not
      // something the arithmetic operators could express.
      if (isObject(arg) && typeof arg['var'] === 'string') return ctx.setCount(arg['var'])
      return 0
    }
    case 'has': {
      if (isObject(arg) && typeof arg['var'] === 'string' && typeof arg['member'] === 'string') {
        return ctx.setHas(arg['var'], arg['member']) ? 1 : 0
      }
      return 0
    }

    // --- logic -------------------------------------------------------------------
    case 'not':
      return evalNode(arg, ctx, depth + 1, budget) === 0 ? 1 : 0
    case 'and': {
      const items = operandList(arg)
      // Short-circuits. An author's `and` that guards an expensive `count` should not pay
      // for it, and the node budget is shared across the whole tree.
      for (const item of items) if (evalNode(item, ctx, depth + 1, budget) === 0) return 0
      return 1
    }
    case 'or': {
      const items = operandList(arg)
      for (const item of items) if (evalNode(item, ctx, depth + 1, budget) !== 0) return 1
      return 0
    }

    // --- comparison ------------------------------------------------------------------
    case 'eq':
    case 'ne':
    case 'lt':
    case 'lte':
    case 'gt':
    case 'gte': {
      const items = operandList(arg)
      if (items.length !== 2) return 0
      const a = evalNode(items[0], ctx, depth + 1, budget)
      const b = evalNode(items[1], ctx, depth + 1, budget)
      switch (op) {
        case 'eq': return a === b ? 1 : 0
        case 'ne': return a !== b ? 1 : 0
        case 'lt': return a < b ? 1 : 0
        case 'lte': return a <= b ? 1 : 0
        case 'gt': return a > b ? 1 : 0
        default: return a >= b ? 1 : 0
      }
    }

    // --- arithmetic ---------------------------------------------------------------------
    case 'add': {
      let sum = 0
      for (const item of operandList(arg)) sum += evalNode(item, ctx, depth + 1, budget)
      return sum
    }
    case 'sub': {
      const items = operandList(arg)
      if (items.length !== 2) return 0
      return evalNode(items[0], ctx, depth + 1, budget) - evalNode(items[1], ctx, depth + 1, budget)
    }
    case 'mul': {
      let product = 1
      for (const item of operandList(arg)) product *= evalNode(item, ctx, depth + 1, budget)
      return product
    }
    case 'min':
    case 'max': {
      const items = operandList(arg)
      if (items.length === 0) return 0
      let best = evalNode(items[0], ctx, depth + 1, budget)
      for (let i = 1; i < items.length; i++) {
        const v = evalNode(items[i], ctx, depth + 1, budget)
        // Explicit comparison rather than Math.min/Math.max, whose NaN and −0 behaviour is
        // the kind of detail that differs between a JavaScript and a C# port.
        if (op === 'min' ? v < best : v > best) best = v
      }
      return best
    }
  }
  return 0
}
